import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  BellAlertIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import axiosInstance from '../utils/axios';

// The triangle says "alarm", the colour says how bad - the same language the
// panel and the phone use, so the three screens read alike.
const severityColor = (severity) => {
  switch (severity) {
    case 'critical':
      return 'text-red-600';
    case 'warning':
      return 'text-orange-500';
    default:
      return 'text-blue-500';
  }
};

// Active is a state rather than a severity, so it wears a ringing bell.
const stateBadge = (state) => {
  switch (state) {
    case 'active':
      return { label: 'Active', className: 'bg-amber-100 text-amber-800', Icon: BellAlertIcon };
    case 'acknowledged':
      return { label: 'Acknowledged', className: 'bg-blue-100 text-blue-800', Icon: CheckCircleIcon };
    default:
      return { label: 'Cleared', className: 'bg-gray-100 text-gray-700', Icon: CheckCircleIcon };
  }
};

const RecentAlarms = ({ limit = 25 }) => {
  const navigate = useNavigate();
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchAlarms = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `/alarms?limit=${limit}&sortBy=timestamp&sortOrder=desc`
      );
      setAlarms(response.data.data || []);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // The panels push their alarm history up on a five minute cycle, so there is
  // nothing to gain from asking more often than once a minute.
  useEffect(() => {
    fetchAlarms();
    const interval = setInterval(fetchAlarms, 60000);
    return () => clearInterval(interval);
  }, [fetchAlarms]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.35 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Recent Alarms</h3>
          <p className="text-sm text-gray-500">The latest {limit} across every Dryer Master, newest first</p>
        </div>
        <button
          onClick={fetchAlarms}
          className="text-gray-400 hover:text-gray-600"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : failed ? (
        <p className="text-sm text-red-600 py-6 text-center">Could not load alarms</p>
      ) : alarms.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">No alarms recorded</p>
      ) : (
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {alarms.map((record) => {
            const badge = stateBadge(record.alarm?.state);
            return (
              <div
                key={record._id}
                onClick={() => navigate(`/dashboard/devices/${record.serialNumber}`)}
                className="flex items-center gap-3 py-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
              >
                <ExclamationTriangleIcon
                  className={`h-5 w-5 flex-shrink-0 ${severityColor(record.alarm?.severity)}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {record.alarm?.message || record.alarm?.type}
                  </p>
                  <p className="text-xs text-gray-500">
                    {record.serialNumber}
                    {record.alarm?.sensorName ? ` · ${record.alarm.sensorName}` : ''}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${badge.className}`}
                >
                  <badge.Icon className="h-3 w-3" />
                  {badge.label}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 w-24 text-right">
                  {record.timestamp
                    ? formatDistanceToNow(new Date(record.timestamp), { addSuffix: true })
                    : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default RecentAlarms;
